// client/src/pages/Settings.tsx
import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Loader2, CheckCircle, XCircle, Copy, Check, X, Shield } from 'lucide-react';
import { buildLoginUrl } from '@/api/auth/oidcConfig';
import ChangePassword from '@/components/ChangePassword';
import FeedbackModal from '@/components/FeedbackModal';
import { settingsService } from '@/api/services/settingsService';
import { requestOrganizationAccess, requestCountryManagerAccess } from '@/api/services/organizationService';
import { Helmet } from "react-helmet-async";
import { useApp } from "@/contexts/AppContext";
import { useTranslation } from "react-i18next";
import type { 
  UserProfile, 
  NotificationPreferences, 
  ConnectedDevice 
} from '@/api/services/settingsService';

export default function AccountSettings() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, setUser, theme } = useApp();
  const { t } = useTranslation();
  
  // Loading and message states
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [operatorRequestLoading, setOperatorRequestLoading] = useState(false);
  const [countryManagerRequestLoading, setCountryManagerRequestLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showRequestFeedback, setShowRequestFeedback] = useState(false);
  const [requestFeedback, setRequestFeedback] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({
    type: 'success',
    title: '',
    message: '',
  });

  // 2FA Modal states
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFaSetupData, setTwoFaSetupData] = useState<{qrCode: string, secret: string} | null>(null);
  const [twoFaVerifyCode, setTwoFaVerifyCode] = useState('');
  const [twoFaVerifying, setTwoFaVerifying] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Data states
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [twoFactorAuth, setTwoFactorAuth] = useState(true);
  const [devices, setDevices] = useState<ConnectedDevice[]>([]);

  // Fetch all settings data
  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      setLoading(true);
      
      const [profileData, notifData, twoFaData, devicesData] = await Promise.all([
        settingsService.getProfile(),
        settingsService.getNotificationPreferences(),
        settingsService.getTwoFactorStatus(),
        settingsService.getDevices(),
      ]);

      setProfile(profileData);
      if (user && profileData) {
        const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
        const nextInitials =
          profileData.firstName && profileData.lastName
            ? `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}`.toUpperCase()
            : user.initials;
        setUser({
          ...user,
          name: fullName || user.name,
          email: profileData.email || user.email,
          initials: nextInitials,
          avatar: profileData.profileImage || user.avatar,
          firstName: profileData.firstName,
          lastName: profileData.lastName,
        });
      }
      setProfileForm({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone || '',
      });
      setEmailNotifications(notifData.emailNotifications);
      setPushNotifications(notifData.pushNotifications);
      setTwoFactorAuth(twoFaData.enabled);
      setDevices(devicesData);
    } catch (error: unknown) {
      showMessage('error', t('operator.settingsPage.messages.loadFailed', { defaultValue: 'Failed to load settings' }));
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const updateData = {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        email: profileForm.email,
        phone: profileForm.phone,
      };
      
      await settingsService.updateProfile(updateData);
      
      showMessage('success', t('operator.settingsPage.messages.profileUpdated', { defaultValue: 'Profile updated successfully!' }));
      
      // Refresh profile data
      const updatedProfile = await settingsService.getProfile();
      setProfile(updatedProfile);
      if (user && updatedProfile) {
        const fullName = `${updatedProfile.firstName} ${updatedProfile.lastName}`.trim();
        const nextInitials =
          updatedProfile.firstName && updatedProfile.lastName
            ? `${updatedProfile.firstName.charAt(0)}${updatedProfile.lastName.charAt(0)}`.toUpperCase()
            : user.initials;
        setUser({
          ...user,
          name: fullName || user.name,
          email: updatedProfile.email || user.email,
          initials: nextInitials,
          avatar: updatedProfile.profileImage || user.avatar,
          firstName: updatedProfile.firstName,
          lastName: updatedProfile.lastName,
        });
      }
    } catch (error: unknown) {
      showMessage('error', (error as { response?: { data?: { message?: string } } }).response?.data?.message || t('operator.settingsPage.messages.updateFailed', { defaultValue: 'Failed to update profile' }));
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle avatar upload
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showMessage('error', t('operator.settingsPage.messages.selectImage', { defaultValue: 'Please select an image file' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', t('operator.settingsPage.messages.imageSize', { defaultValue: 'Image size must be less than 5MB' }));
      return;
    }

    setAvatarUploading(true);

    try {
      const response = await settingsService.uploadAvatar(file);
      showMessage('success', '✅ ' + t('operator.settingsPage.messages.avatarUpdated', { defaultValue: 'Profile picture updated successfully!' }));
      
      if (profile) {
        const avatarUrl = response.data?.avatarUrl || response.avatarUrl || response.data?.url || response.url || response.avatar || response.data?.avatar;
        if (avatarUrl) {
          setProfile({ ...profile, profileImage: avatarUrl });
          if (user) {
            setUser({ ...user, avatar: avatarUrl });
          }
        } else {
          const updatedProfile = await settingsService.getProfile();
          setProfile(updatedProfile);
          if (updatedProfile?.profileImage && user) {
            setUser({ ...user, avatar: updatedProfile.profileImage });
          }
        }
      }
    } catch (error: unknown) {
      const errorMsg = (error as { response?: { data?: { message?: string, error?: string } } }).response?.data?.message || (error as { response?: { data?: { message?: string, error?: string } } }).response?.data?.error || 'Failed to upload avatar';
      showMessage('error', `❌ ${errorMsg}`);
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle notification toggle
  const handleEmailNotificationToggle = async () => {
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);

    try {
      await settingsService.updateNotificationPreferences({ emailNotifications: newValue });
      showMessage('success', t('operator.settingsPage.messages.notificationUpdated', { defaultValue: 'Notification preferences updated' }));
    } catch (error: unknown) {
      setEmailNotifications(!newValue);
      showMessage('error', t('operator.settingsPage.messages.notificationUpdateFailed', { defaultValue: 'Failed to update notification preferences' }));
    }
  };

  const handlePushNotificationToggle = async () => {
    const newValue = !pushNotifications;
    setPushNotifications(newValue);

    try {
      await settingsService.updateNotificationPreferences({ pushNotifications: newValue });
      showMessage('success', t('operator.settingsPage.messages.notificationUpdated', { defaultValue: 'Notification preferences updated' }));
    } catch (error: unknown) {
      setPushNotifications(!newValue);
      showMessage('error', t('operator.settingsPage.messages.notificationUpdateFailed', { defaultValue: 'Failed to update notification preferences' }));
    }
  };

  // Handle 2FA toggle
  const handle2FAToggle = async () => {
    if (twoFactorAuth) {
      if (!confirm(t('operator.settingsPage.messages.confirmDisable2fa', { defaultValue: 'Are you sure you want to disable two-factor authentication?' }))) {
        return;
      }
      try {
        await settingsService.disableTwoFactor({});
        setTwoFactorAuth(false);
        showMessage('success', '✅ ' + t('operator.settingsPage.messages.twoFaDisabled', { defaultValue: 'Two-factor authentication disabled. Your account is less secure.' }));
      } catch (error: unknown) {
        showMessage('error', '❌ ' + t('operator.settingsPage.messages.twoFaDisableFailed', { defaultValue: 'Failed to disable 2FA. Please try again.' }));
      }
    } else {
      try {
        const emailToUse = user?.email || profile?.email;
        if (!emailToUse) {
           showMessage('error', '❌ ' + t('operator.settingsPage.messages.emailNotFound', { defaultValue: 'User email not found. Please refresh the page.' }));
           return;
        }
        const response: Record<string, unknown> = await settingsService.enableTwoFactor({ email: emailToUse });
        
        const resData = response as Record<string, unknown>;
        if (resData?.success || (resData?.data as Record<string, unknown>)?.success || resData?.success !== false) {
           setTwoFactorAuth(true);
           // Redirect to the IdP to enforce MFA setup right away (provider-portable).
           window.location.href = buildLoginUrl({
             role: user?.role || 'operator',
             prompt: 'login',
             acrValues: 'multi-factor',
           });
        } else {
           showMessage('error', '❌ ' + t('operator.settingsPage.messages.twoFaEnableFailed', { defaultValue: 'Failed to enable 2FA. Please try again.' }));
        }
      } catch (error: unknown) {
        showMessage('error', '❌ ' + t('operator.settingsPage.messages.twoFaEnableFailed', { defaultValue: 'Failed to enable 2FA. Please try again.' }));
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const submit2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (twoFaVerifyCode.length !== 6) return;
    
    const emailToUse = user?.email || profile?.email;
    if (!emailToUse) {
      showMessage('error', '❌ ' + t('operator.settingsPage.messages.emailNotFound', { defaultValue: 'User email not found. Please refresh the page.' }));
      return;
    }
    
    setTwoFaVerifying(true);
    try {
      await settingsService.verifyTwoFactor(emailToUse, twoFaVerifyCode);
      setTwoFactorAuth(true);
      setShow2FAModal(false);
      showMessage('success', '✅ ' + t('operator.settingsPage.messages.twoFaEnabled', { defaultValue: 'Two-factor authentication enabled successfully!' }));
    } catch (error: unknown) {
       showMessage('error', (error as {response?: {data?: {message?: string}}})?.response?.data?.message || '❌ Invalid verification code. Please try again.');
    } finally {
      setTwoFaVerifying(false);
    }
  };

  // Handle device removal
  const handleRemoveDevice = async (deviceId: string) => {
    if (!confirm(t('operator.settingsPage.messages.confirmRemoveDevice', { defaultValue: 'Are you sure you want to remove this device?' }))) {
      return;
    }

    try {
      await settingsService.removeDevice(deviceId);
      setDevices(devices.filter(d => d._id !== deviceId));
      showMessage('success', t('operator.settingsPage.messages.deviceRemoved', { defaultValue: 'Device removed successfully' }));
    } catch (error: unknown) {
      showMessage('error', t('operator.settingsPage.messages.deviceRemoveFailed', { defaultValue: 'Failed to remove device' }));
    }
  };

  const handleRequestOrganizationAccess = async () => {
    try {
      setOperatorRequestLoading(true);
      await requestOrganizationAccess();

      setRequestFeedback({
        type: 'success',
        title: t('operator.settingsPage.operatorRequest.successTitle', {
          defaultValue: 'Request Sent',
        }),
        message: t('operator.settingsPage.operatorRequest.success', {
          defaultValue: 'Your organization request has been sent successfully.',
        }),
      });
      setShowRequestFeedback(true);
    } catch (error: unknown) {
      const errObj = error as { message?: string };
      setRequestFeedback({
        type: 'error',
        title: t('operator.settingsPage.operatorRequest.failedTitle', {
          defaultValue: 'Request Failed',
        }),
        message: errObj?.message || t('operator.settingsPage.operatorRequest.failed', {
          defaultValue: 'Failed to send organization request.',
        }),
      });
      setShowRequestFeedback(true);
    } finally {
      setOperatorRequestLoading(false);
    }
  };

  const handleRequestCountryManagerAccess = async () => {
    try {
      setCountryManagerRequestLoading(true);
      await requestCountryManagerAccess({
        country: profile?.managedCountry || profile?.country || user?.country || 'NG'
      });

      setRequestFeedback({
        type: 'success',
        title: t('operator.settingsPage.countryManagerRequest.successTitle', {
          defaultValue: 'Request Sent',
        }),
        message: t('operator.settingsPage.countryManagerRequest.success', {
          defaultValue: 'Your country manager request has been sent successfully.',
        }),
      });
      setShowRequestFeedback(true);
    } catch (error: unknown) {
      const errObj = error as { message?: string };
      setRequestFeedback({
        type: 'error',
        title: t('operator.settingsPage.countryManagerRequest.failedTitle', {
          defaultValue: 'Request Failed',
        }),
        message: errObj?.message || t('operator.settingsPage.countryManagerRequest.failed', {
          defaultValue: 'Failed to send country manager request.',
        }),
      });
      setShowRequestFeedback(true);
    } finally {
      setCountryManagerRequestLoading(false);
    }
  };

  const formatLastActive = (dateString?: string | null) => {
    if (!dateString) return t('operator.settingsPage.common.na', { defaultValue: 'N/A' });
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return t('operator.settingsPage.common.na', { defaultValue: 'N/A' });
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 0) return t('operator.settingsPage.time.justNow', { defaultValue: 'Just now' });
    if (diffInMinutes < 1) return t('operator.settingsPage.time.justNow', { defaultValue: 'Just now' });
    if (diffInMinutes < 60) return t('operator.settingsPage.time.minutesAgo', { defaultValue: '{{count}} minute ago', count: diffInMinutes });
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return t('operator.settingsPage.time.hoursAgo', { defaultValue: '{{count}} hour ago', count: diffInHours });
    
    const diffInDays = Math.floor(diffInHours / 24);
    return t('operator.settingsPage.time.daysAgo', { defaultValue: '{{count}} day ago', count: diffInDays });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('operator.settingsPage.meta.title', { defaultValue: 'Account Settings | KonectX' })}</title>
        <meta
          name="description"
          content={t('operator.settingsPage.meta.description', { defaultValue: 'Manage your KonectX account settings. Update your profile, security preferences, and notification settings securely.' })}
        />
        <meta
          name="keywords"
          content={t('operator.settingsPage.meta.keywords', { defaultValue: 'KonectX, account settings, profile, security, notifications, user preferences' })}
        />
        <meta property="og:title" content={t('operator.settingsPage.meta.ogTitle', { defaultValue: 'Account Settings - KonectX' })} />
        <meta
          property="og:description"
          content={t('operator.settingsPage.meta.description', { defaultValue: 'Manage your KonectX account settings. Update your profile, security preferences, and notification settings securely.' })}
        />
      </Helmet>

    <div className="min-h-screen bg-gray-50 dark:bg-black">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-5 py-5">
        {/* Global Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Profile Section */}
            <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  {profile?.profileImage ? (
                    <img 
                      src={profile.profileImage} 
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover ring-4 ring-gray-200 dark:ring-gray-700 transition-all duration-300 hover:scale-105"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center ring-4 ring-gray-200 dark:ring-gray-700 transition-all duration-300 hover:bg-gray-200 dark:hover:bg-gray-700 group">
                      <User className="w-12 h-12 text-gray-400 dark:text-gray-500 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  )}
                  <button 
                    onClick={handleAvatarClick}
                    disabled={avatarUploading}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-gray-800 dark:bg-blue-600 rounded-full flex items-center justify-center border-4 border-white dark:border-gray-900 hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {avatarUploading ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                    {profile?.firstName} {profile?.lastName}
                  </h2>
                  <p className="text-lg text-gray-500 dark:text-gray-300 capitalize">
                    {profile?.role || t('operator.settingsPage.profile.user', { defaultValue: 'User' })}
                  </p>
                </div>
              </div>
                  
              {/* Form Fields - Profile Info Only */}
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">{t('operator.settingsPage.profile.fullName', { defaultValue: 'Full Name' })}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                       placeholder={t('operator.settingsPage.profile.enterFullName', { defaultValue: 'Enter your full Name' })}
                      value={`${profileForm.firstName} ${profileForm.lastName}`}
                      onChange={(e) => {
                        const names = e.target.value.split(' ');
                        setProfileForm({
                          ...profileForm,
                          firstName: names[0] || '',
                          lastName: names.slice(1).join(' ') || ''
                        });
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-[#1a1b1e] rounded-md text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-blue-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">{t('operator.settingsPage.profile.emailOrPhone', { defaultValue: 'Email or Phone' })}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                       placeholder={t('operator.settingsPage.profile.enterEmailOrPhone', { defaultValue: 'Enter your Email or Phone' })}
                      value={profileForm.email || profileForm.phone}
                      disabled={true}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.includes('@')) {
                          setProfileForm({ ...profileForm, email: value });
                        } else {
                          setProfileForm({ ...profileForm, phone: value });
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-[#1a1b1e] rounded-md text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-blue-600"
                    />
                  </div>
                </div>

                {/* Save Profile Button */}
                <div className="mt-6">
                  <button 
                    type="submit"
                    disabled={profileLoading}
                    className="w-full px-6 py-3 bg-gray-900 text-white dark:bg-blue-700 text-sm rounded-md hover:bg-gray-800 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('operator.settingsPage.actions.updating', { defaultValue: 'Updating...' })}
                      </>
                    ) : (
                       t('operator.settingsPage.actions.updateProfile', { defaultValue: 'Update Profile' })
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Change Password Section - Now using component */}
            <ChangePassword />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Notification Settings */}
            <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">{t('operator.settingsPage.notifications.title', { defaultValue: 'Notification Settings' })}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-300 mb-6">{t('operator.settingsPage.notifications.subtitle', { defaultValue: 'Manage how you receive notifications.' })}</p>

              <div className="space-y-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-white mb-0.5">{t('operator.settingsPage.notifications.emailTitle', { defaultValue: 'Email Notifications' })}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-300">{t('operator.settingsPage.notifications.emailDesc', { defaultValue: 'Receive notifications via email' })}</p>
                  </div>
                  <button
                    onClick={handleEmailNotificationToggle}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      emailNotifications ? 'bg-gray-800 dark:bg-blue-700' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        emailNotifications ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-white mb-0.5">{t('operator.settingsPage.notifications.pushTitle', { defaultValue: 'Push Notifications' })}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-300">{t('operator.settingsPage.notifications.pushDesc', { defaultValue: 'Receive push notifications on your devices' })}</p>
                  </div>
                  <button
                    onClick={handlePushNotificationToggle}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      pushNotifications ? 'bg-gray-800 dark:bg-blue-700' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        pushNotifications ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">{t('operator.settingsPage.security.title', { defaultValue: 'Security Settings' })}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-300 mb-6">{t('operator.settingsPage.security.subtitle', { defaultValue: 'Manage your account security settings.' })}</p>

              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-base font-semibold text-gray-800 dark:text-white mb-0.5">{t('operator.settingsPage.security.twoFactor', { defaultValue: 'Two-Factor Authentication' })}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-300">{t('operator.settingsPage.security.twoFactorDesc', { defaultValue: 'Add an extra layer of security to your account' })}</p>
                </div>
                <button
                  onClick={handle2FAToggle}
                  title={twoFactorAuth ? '✅ 2FA Enabled - Click to disable' : '❌ 2FA Disabled - Click to enable'}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    twoFactorAuth ? 'bg-green-600 dark:bg-green-700' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      twoFactorAuth ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
                {t('operator.settingsPage.operatorRequest.title', { defaultValue: 'Organisation Request' })}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-300 mb-6">
                {t('operator.settingsPage.operatorRequest.subtitle', { defaultValue: 'Send a request if you want your operator account to become an organisation.' })}
              </p>

              <button
                type="button"
                onClick={handleRequestOrganizationAccess}
                disabled={operatorRequestLoading}
                className="w-full px-6 py-3 bg-gray-900 text-white dark:bg-blue-700 text-sm rounded-md hover:bg-gray-800 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {operatorRequestLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('operator.settingsPage.operatorRequest.sending', { defaultValue: 'Sending Request...' })}
                  </>
                ) : (
                  t('operator.settingsPage.operatorRequest.button', { defaultValue: 'Request to Become an Organisation' })
                )}
              </button>
            </div>

            {/* Country Manager Request */}
            <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
                {t('operator.settingsPage.countryManagerRequest.title', { defaultValue: 'Country Manager Request' })}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-300 mb-6">
                {t('operator.settingsPage.countryManagerRequest.subtitle', { defaultValue: 'Send a request if you want your operator account to become a country manager.' })}
              </p>

              <button
                type="button"
                onClick={handleRequestCountryManagerAccess}
                disabled={countryManagerRequestLoading}
                className="w-full px-6 py-3 bg-gray-900 text-white dark:bg-blue-700 text-sm rounded-md hover:bg-gray-800 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {countryManagerRequestLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('operator.settingsPage.countryManagerRequest.sending', { defaultValue: 'Sending Request...' })}
                  </>
                ) : (
                  t('operator.settingsPage.countryManagerRequest.button', { defaultValue: 'Request to Become a Country Manager' })
                )}
              </button>
            </div>

            {/* Connected Devices */}
            <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{t('operator.settingsPage.devices.title', { defaultValue: 'Connected Devices' })}</h3>
              {devices.length === 0 ? (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                  {t('operator.settingsPage.devices.none', { defaultValue: 'No connected devices' })}
                </p>
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <div key={device._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#1a1b1e] rounded-md">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {device.deviceType.toLowerCase().includes('mobile') || device.deviceType.toLowerCase().includes('phone') ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          )}
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-white">
                            {device.deviceName}
                            {device.isCurrent && (
                              <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-0.5 rounded">
                                {t('operator.settingsPage.devices.current', { defaultValue: 'Current' })}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('operator.settingsPage.devices.lastActive', { defaultValue: 'Last active' })}: {formatLastActive(device.lastActive)}
                          </p>
                        </div>
                      </div>
                      {!device.isCurrent && (
                        <button 
                          onClick={() => handleRemoveDevice(device._id)}
                          className="w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                        >
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
      <FeedbackModal
        isOpen={showRequestFeedback}
        onClose={() => setShowRequestFeedback(false)}
        type={requestFeedback.type}
        title={requestFeedback.title}
        message={requestFeedback.message}
      />

      {/* Enable 2FA Modal */}
      {show2FAModal && twoFaSetupData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#202123] rounded-2xl w-full max-w-md overflow-hidden shadow-2xl relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShow2FAModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
            >
              <XCircle className="w-5 h-5" />
            </button>

            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                {t('operator.settingsPage.twoFaModal.title', { defaultValue: 'Configure Two-Factor Authentication' })}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                {t('operator.settingsPage.twoFaModal.description', { defaultValue: 'Scan the QR code below with an authenticator app (like Google Authenticator or Authy) to enable 2FA.' })}
              </p>

              <div className="flex justify-center mb-6 p-4 bg-white rounded-xl">
                <img src={twoFaSetupData.qrCode} alt="2FA QR Code" className="w-48 h-48 object-contain" />
              </div>

              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('operator.settingsPage.twoFaModal.manualKey', { defaultValue: 'Or enter this setup key manually:' })}</p>
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-[#1a1b1e] p-3 rounded-lg border border-gray-200 dark:border-gray-800">
                  <code className="text-blue-600 dark:text-blue-400 font-mono text-sm tracking-wider flex-1 overflow-x-auto whitespace-nowrap">
                    {twoFaSetupData.secret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(twoFaSetupData.secret)}
                    className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
                   
                  >
                    {copySuccess ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                </div>
              </div>

              <form onSubmit={submit2FAVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {t('operator.settingsPage.twoFaModal.verificationCode', { defaultValue: 'Verification Code' })}
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    placeholder={t('operator.settingsPage.twoFaModal.enterCode', { defaultValue: 'Enter 6-digit code' })}
                    value={twoFaVerifyCode}
                    onChange={(e) => setTwoFaVerifyCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1a1b1e] rounded-xl text-center text-xl tracking-widest border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  disabled={twoFaVerifying || twoFaVerifyCode.length !== 6}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {twoFaVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('operator.settingsPage.twoFaModal.verifying', { defaultValue: 'Verifying...' })}
                    </>
                  ) : (
                    t('operator.settingsPage.twoFaModal.verifyBtn', { defaultValue: 'Verify & Enable 2FA' })
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    
{show2FAModal && twoFaSetupData && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-300">
        <div 
          className={`w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl transition-all duration-300 transform scale-100 ${
            theme === 'dark' ? 'bg-[#333436] border border-gray-700' : 'bg-white'
          }`}
        >
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.profile.twofa.modalTitle', { defaultValue: 'Secure Your Account' })}
              </h3>
              <button 
                onClick={() => setShow2FAModal(false)}
                className={`p-1 rounded-full transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div className={`p-4 rounded-2xl flex items-start gap-4 ${theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                <Shield className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
                <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
                  {t('enduser.profile.twofa.modalDesc', { defaultValue: 'Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.) to get started.' })}
                </p>
              </div>

              <div className="flex justify-center p-4 bg-white rounded-2xl">
                <img 
                  src={twoFaSetupData.qrCode} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                />
              </div>

              <div className="space-y-2">
                <p className={`text-xs font-semibold uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('enduser.profile.twofa.manualKey', { defaultValue: 'Manual Setup Key' })}
                </p>
                <div className={`flex items-center justify-between p-4 rounded-xl border ${theme === 'dark' ? 'bg-black/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <code className={`text-sm font-mono font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    {twoFaSetupData.secret}
                  </code>
                  <button 
                    onClick={() => copyToClipboard(twoFaSetupData.secret)}
                    className="flex items-center gap-2 text-xs font-medium text-blue-500 hover:text-blue-600 transition-colors"
                  >
                    {copySuccess ? (
                      <><CheckCircle size={16} /> {t('enduser.profile.twofa.copied', { defaultValue: 'Copied!' })}</>
                    ) : (
                      <><Copy size={16} /> {t('enduser.profile.twofa.copy', { defaultValue: 'Copy' })}</>
                    )}
                  </button>
                </div>
              </div>

              <form onSubmit={submit2FAVerification} className="space-y-4">
                <div className="space-y-2">
                  <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('enduser.profile.twofa.enterCode', { defaultValue: 'Verification Code' })}
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={twoFaVerifyCode}
                    onChange={(e) => setTwoFaVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className={`w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] rounded-xl outline-none transition-all ${
                      theme === 'dark' 
                        ? 'bg-black border-gray-700 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-600'
                    } border-2`}
                    disabled={twoFaVerifying}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={twoFaVerifying || twoFaVerifyCode.length !== 6}
                  className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {twoFaVerifying ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> {t('enduser.profile.twofa.verifying', { defaultValue: 'Verifying...' })}</>
                  ) : (
                    t('enduser.profile.twofa.enableBtn', { defaultValue: 'Verify & Enable 2FA' })
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    )}

  
</>
  );
}
